import i18next from 'i18next';
import {
  CONTACT_ISSUE_CHANNEL,
  type ContactIssue,
  DELIVERY_METHOD,
  type Member,
  type MemberContactIssues,
} from '@/types';

const CONTACT_ISSUE_CHANNEL_TRANSLATION_KEYS: Record<CONTACT_ISSUE_CHANNEL, string> = {
  [CONTACT_ISSUE_CHANNEL.EMAIL]: 'members.contactIssues.channelLabels.email',
  [CONTACT_ISSUE_CHANNEL.TEXT]: 'members.contactIssues.channelLabels.text',
};

const CONTACT_ISSUE_DELIVERY_METHOD_TURNED_OFF_TRANSLATION_KEYS: Record<CONTACT_ISSUE_CHANNEL, string> = {
  [CONTACT_ISSUE_CHANNEL.EMAIL]: 'members.contactIssues.deliveryMethodTurnedOff.email',
  [CONTACT_ISSUE_CHANNEL.TEXT]: 'members.contactIssues.deliveryMethodTurnedOff.text',
};

export const getDeliveryMethodByContactIssueChannel = (channel: CONTACT_ISSUE_CHANNEL): DELIVERY_METHOD =>
  channel === CONTACT_ISSUE_CHANNEL.EMAIL ? DELIVERY_METHOD.EMAIL : DELIVERY_METHOD.TEXT;

export const getContactIssueAlertMessage = (issue: ContactIssue): string => {
  const reason = getContactIssueReasonLabel(issue.reasonCode);

  return issue.consecutiveCount === null
    ? reason
    : i18next.t('members.contactIssues.reasonWithConsecutiveCount', {
        ns: 'global',
        reason,
        count: issue.consecutiveCount,
      });
};

export const getConflictedDeliveryMethods = (contactIssues: ContactIssue[]): DELIVERY_METHOD[] => {
  const conflictedMethods = new Set<DELIVERY_METHOD>();

  contactIssues.forEach((issue) => {
    conflictedMethods.add(getDeliveryMethodByContactIssueChannel(issue.channel));
  });

  return Array.from(conflictedMethods);
};

export const getContactIssueReasonLabel = (reasonCode: string): string =>
  i18next.t(`members.contactIssues.reasonCodes.${reasonCode}`, { ns: 'global', defaultValue: reasonCode });

export const getContactIssueChannelLabel = (channel: CONTACT_ISSUE_CHANNEL): string =>
  i18next.t(CONTACT_ISSUE_CHANNEL_TRANSLATION_KEYS[channel], { ns: 'global', defaultValue: channel });

export const getContactIssueDeliveryMethodTurnedOffLabel = (channel: CONTACT_ISSUE_CHANNEL): string =>
  i18next.t(CONTACT_ISSUE_DELIVERY_METHOD_TURNED_OFF_TRANSLATION_KEYS[channel], { ns: 'global' });

export const hasContactInfoForChannel = ({
  member,
  channel,
}: {
  member: Pick<Member, 'email' | 'phone'>;
  channel: CONTACT_ISSUE_CHANNEL;
}): boolean =>
  channel === CONTACT_ISSUE_CHANNEL.EMAIL ? Boolean(member.email?.trim()) : Boolean(member.phone?.trim());

export const removeContactIssueFromMember = ({
  member,
  issueId,
}: {
  member: Omit<Member, 'id'>;
  issueId: string;
}): Omit<Member, 'id'> => {
  const remainingIssues = getMemberContactIssues(member).filter((issue) => issue.id !== issueId);

  return {
    ...member,
    hasContactIssue: remainingIssues.length > 0,
    contactIssues: remainingIssues,
  };
};

export const restoreDeliveryMethodAfterContactIssueClear = ({
  member,
  channel,
}: {
  member: Omit<Member, 'id'>;
  channel: CONTACT_ISSUE_CHANNEL;
}): Omit<Member, 'id'> => {
  if (!hasContactInfoForChannel({ member, channel })) {
    return member;
  }

  const deliveryMethod = getDeliveryMethodByContactIssueChannel(channel);
  const defaultDeliveryMethods = member.defaultDeliveryMethods || [];
  const nextDefaultDeliveryMethods = defaultDeliveryMethods.includes(deliveryMethod)
    ? defaultDeliveryMethods
    : [...defaultDeliveryMethods, deliveryMethod];

  return {
    ...member,
    defaultDeliveryMethods: nextDefaultDeliveryMethods,
    groups: member.groups?.map((group) => {
      if (group.deliveryMethods.includes(deliveryMethod)) {
        return group;
      }

      return {
        ...group,
        deliveryMethods: [...group.deliveryMethods, deliveryMethod],
      };
    }),
  };
};

export const applyContactIssueClear = ({
  member,
  issue,
}: {
  member: Omit<Member, 'id'>;
  issue: ContactIssue;
}): Omit<Member, 'id'> => {
  const memberWithoutIssue = removeContactIssueFromMember({ member, issueId: issue.id });
  const hasRemainingChannelIssues =
    getContactIssuesByChannel({
      contactIssues: getMemberContactIssues(memberWithoutIssue),
      channel: issue.channel,
    }).length > 0;

  if (hasRemainingChannelIssues) {
    return memberWithoutIssue;
  }

  return restoreDeliveryMethodAfterContactIssueClear({
    member: memberWithoutIssue,
    channel: issue.channel,
  });
};

export const getContactIssuesByChannel = ({
  contactIssues,
  channel,
}: {
  contactIssues: ContactIssue[];
  channel: CONTACT_ISSUE_CHANNEL;
}): ContactIssue[] => contactIssues.filter((issue) => issue.channel === channel);

export const hasContactIssueForChannel = ({
  contactIssues,
  channel,
}: {
  contactIssues: ContactIssue[];
  channel: CONTACT_ISSUE_CHANNEL;
}): boolean => getContactIssuesByChannel({ contactIssues, channel }).length > 0;

export const getMemberContactIssues = (member: Partial<MemberContactIssues>): ContactIssue[] =>
  member.hasContactIssue ? (member.contactIssues ?? []) : [];

export const getContactIssueTooltipLabel = (issues: ContactIssue[]): string =>
  issues.map(getContactIssueAlertMessage).join('\n');
